import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function fmt(cents) {
  return '$' + Math.abs(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  });
}

function UtilizationBar({ pct }) {
  const color = pct > 80 ? '#ff082f' : pct > 60 ? '#f59e0b' : '#3a6ad9';
  return (
    <div style={{ background: '#f3f4f6', borderRadius: 999, height: 8, width: '100%', overflow: 'hidden' }}>
      <div style={{
        width: `${Math.min(pct, 100)}%`, height: '100%',
        background: color, borderRadius: 999, transition: 'width 0.3s ease',
      }} />
    </div>
  );
}

export default function AdminAcquisition() {
  const [facility, setFacility] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [showDraw, setShowDraw] = useState(false);
  const [drawForm, setDrawForm] = useState({ loan_id: '', drawn_amount_cents: '', memo: '' });
  const [submitting, setSubmitting] = useState(false);

  const token = localStorage.getItem('doorly_admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/credit-facilities`, { headers });
      const acq = (res.data || []).find(f => f.facility_type === 'acquisition' && f.active);
      setFacility(acq || null);
    } catch (err) {
      console.error('Acquisition fetch failed:', err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDraw = async () => {
    if (!drawForm.drawn_amount_cents) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/api/admin/credit-facilities/${facility.id}/draw`,
        {
          ...drawForm,
          drawn_amount_cents: Math.round(parseFloat(drawForm.drawn_amount_cents) * 100),
          loan_id: drawForm.loan_id || null,
        },
        { headers }
      );
      setShowDraw(false);
      setDrawForm({ loan_id: '', drawn_amount_cents: '', memo: '' });
      await fetchData();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayoff = async (positionId) => {
    const amount = window.prompt('Payoff amount ($):');
    if (!amount) return;
    try {
      await axios.patch(
        `${API}/api/admin/credit-facilities/positions/${positionId}/payoff`,
        { paid_off_amount_cents: Math.round(parseFloat(amount) * 100) },
        { headers }
      );
      await fetchData();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    }
  };

  if (loading) return (
    <AdminLayout>
      <div style={{ padding: '24px 32px', color: '#6b7280', fontSize: 13 }}>Loading...</div>
    </AdminLayout>
  );

  if (!facility) return (
    <AdminLayout>
      <div style={{ padding: '24px 32px' }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827' }}>Acquisition</h1>
        <div style={{
          background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10,
          padding: 32, textAlign: 'center', color: '#6b7280', fontSize: 13, marginTop: 16,
        }}>
          No acquisition credit facility configured
        </div>
      </div>
    </AdminLayout>
  );

  const allInRate = parseFloat(facility.rate_spread || 0) + parseFloat(facility.current_index_rate || 0);

  return (
    <AdminLayout>
      <div style={{ padding: '24px 32px' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
          <div>
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 2 }}>Finance</div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: 0 }}>Acquisition</h1>
          </div>
          <button onClick={() => setShowDraw(s => !s)}
            style={{ background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            + Record Draw
          </button>
        </div>

        {/* Draw form */}
        {showDraw && (
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '20px 22px', marginBottom: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 14 }}>Record Acquisition Draw</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 14 }}>
              {[
                { key: 'drawn_amount_cents', label: 'Amount ($)', type: 'number' },
                { key: 'loan_id', label: 'Loan ID (optional)', type: 'text' },
                { key: 'memo', label: 'Memo', type: 'text' },
              ].map(f => (
                <div key={f.key}>
                  <label style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, display: 'block', marginBottom: 3 }}>{f.label}</label>
                  <input type={f.type} value={drawForm[f.key]}
                    onChange={e => setDrawForm(d => ({ ...d, [f.key]: e.target.value }))}
                    style={{ width: '100%', padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 12, boxSizing: 'border-box' }} />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowDraw(false)}
                style={{ background: '#fff', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 8, padding: '7px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                Cancel
              </button>
              <button disabled={submitting || !drawForm.drawn_amount_cents} onClick={handleDraw}
                style={{
                  background: (submitting || !drawForm.drawn_amount_cents) ? '#9ca3af' : '#3a6ad9',
                  color: '#fff', border: 'none', borderRadius: 8, padding: '7px 16px',
                  fontSize: 12, fontWeight: 600,
                  cursor: (submitting || !drawForm.drawn_amount_cents) ? 'default' : 'pointer',
                }}>
                {submitting ? 'Recording...' : 'Record Draw'}
              </button>
            </div>
          </div>
        )}

        {/* Facility summary card */}
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '20px 22px', marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{facility.lender_name}</div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>{facility.name}</div>
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', textAlign: 'right' }}>
              {facility.rate_index} + {facility.rate_spread}% = <strong>{allInRate.toFixed(2)}% all-in</strong>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
            {[
              { label: 'Credit Limit', value: fmt(facility.credit_limit_cents) },
              { label: 'Balance Drawn', value: fmt(facility.balance_cents), highlight: facility.balance_cents > 0 },
              { label: 'Available', value: fmt(facility.available_cents) },
              { label: 'Active Loans', value: facility.active_positions?.length || 0 },
            ].map(m => (
              <div key={m.label} style={{ background: '#f9fafb', borderRadius: 8, padding: '12px 14px' }}>
                <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{m.label}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: m.highlight ? '#3a6ad9' : '#111827' }}>{m.value}</div>
              </div>
            ))}
          </div>

          <div style={{ marginBottom: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#6b7280', marginBottom: 6 }}>
              <span>Utilization</span>
              <span style={{ fontWeight: 700, color: '#111827' }}>{facility.utilization_pct}%</span>
            </div>
            <UtilizationBar pct={facility.utilization_pct} />
          </div>

          {facility.daily_interest_cents > 0 && (
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 8 }}>
              Daily interest at current balance: <strong style={{ color: '#111827' }}>{fmt(facility.daily_interest_cents)}</strong>
            </div>
          )}
        </div>

        {/* Active positions */}
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ padding: '14px 22px', borderBottom: '1px solid #f3f4f6', fontSize: 13, fontWeight: 700, color: '#111827' }}>
            Active Positions
            {facility.active_positions?.length > 0 && (
              <span style={{ marginLeft: 8, fontSize: 11, color: '#6b7280', fontWeight: 400 }}>
                ({facility.active_positions.length} loan{facility.active_positions.length !== 1 ? 's' : ''})
              </span>
            )}
          </div>

          {(!facility.active_positions || facility.active_positions.length === 0) ? (
            <div style={{ padding: '24px', textAlign: 'center', color: '#9ca3af', fontSize: 12, fontStyle: 'italic' }}>
              No active positions — all A-B loans have been paid off
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  {['Loan', 'Borrower', 'Amount', 'Drawn', 'Days on Line', ''].map(h => (
                    <th key={h} style={{ padding: '8px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {facility.active_positions.map((pos, i) => (
                  <tr key={pos.id} style={{ borderTop: i > 0 ? '1px solid #f3f4f6' : 'none' }}>
                    <td style={{ padding: '10px 16px', fontSize: 12, fontWeight: 600, color: '#3a6ad9' }}>{pos.loan_number || '—'}</td>
                    <td style={{ padding: '10px 16px', fontSize: 12, color: '#374151' }}>{pos.borrower_name || '—'}</td>
                    <td style={{ padding: '10px 16px', fontSize: 12, fontWeight: 700, color: '#111827' }}>{fmt(pos.drawn_amount_cents)}</td>
                    <td style={{ padding: '10px 16px', fontSize: 12, color: '#6b7280' }}>
                      {new Date(pos.drawn_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </td>
                    <td style={{ padding: '10px 16px' }}>
                      <span style={{
                        fontSize: 11, fontWeight: 600,
                        background: pos.days_on_line > 1 ? '#fef2f2' : '#f0fdf4',
                        color: pos.days_on_line > 1 ? '#991b1b' : '#166534',
                        borderRadius: 6, padding: '2px 8px',
                      }}>
                        {pos.days_on_line === 0 ? 'Today' : `${pos.days_on_line}d`}
                      </span>
                    </td>
                    <td style={{ padding: '10px 16px' }}>
                      <button onClick={() => handlePayoff(pos.id)}
                        style={{ background: '#fff', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                        Record Payoff
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
